import _ from 'underscore';

import { updateCaptionsOffset } from 'redux/modules/embed/actions/captions';
import { Dispatch, GetState } from 'redux/types';
import embedUtils from '../../../../utils/embed';
import { max } from '../../../../utils/numbers';
import { actions as embedConfigurationActions } from '../../../middleware/api/embed-configuration-service';
import { autosaveActionBuilder } from '../../../middleware/autosave';
import { withAutosaveDrivenMeta } from '../../../middleware/embed-preview-sync';
import * as authSelectors from '../../auth/selectors';
import * as notificationActions from '../../notification';
import { actions as projectActions } from '../../project';
import * as routingActions from '../../router/actions';
import * as types from '../action-types';
import * as embedSelectors from '../selectors';
import { processIntroOutroClipUploads } from './edge-videos';
import { saveDirtyLayouts } from './layout';

const setEmbedDurationAction = (durationMillis: number) => ({
  type: types.EMBED_DURATION_SET,
  payload: { durationMillis },
});

export const getEmbedConfiguration = (wid: string) =>
  embedConfigurationActions.getEmbedConfiguration(wid);

export const clearEmbedState = () => ({
  type: types.EMBED_STATE_CLEAR,
});

export const createEmbedCode = (wid: string, recordingId?: number) => {
  const code = embedUtils.createEmbedCode(wid, recordingId);

  return {
    type: types.EMBED_CODE_CREATE,
    payload: { code },
  };
};

export const setEmbedConfiguration = (configuration: any) => (
  dispatch: Dispatch,
) => {
  const { wid } = configuration;
  dispatch({
    type: types.EMBED_CONFIGURATION_SET,
    payload: { configuration },
  });
  dispatch(routingActions.updateEditorUrl(wid));
};

export const createEmbedConfiguration = (config?: any) => (
  dispatch: Dispatch,
  getState: GetState,
) => {
  const configuration =
    config || embedSelectors.mapStateToEmbedConfig(getState());

  const userId = authSelectors.userIdSelector(getState());
  const {
    edgeVideos,
    expectedDurationMilli,
    slideshowInfo,
    textOverlayInfo,
    audioInfo,
    versionInfo,
    dimensions,
    soundwave,
    watermark,
    captions,
    recordingId,
    videoClips,
    layerOrder,
    lockSpec,
    mainMediaContainer,
    mediaSegmentInfo,
    progress,
    timer,
  } = configuration;

  return dispatch(
    embedConfigurationActions.createEmbedConfiguration({
      durationMillis: expectedDurationMilli,
      slides: slideshowInfo,
      textOverlayInfo,
      audioInfo,
      videoClips,
      versionInfo,
      recordingId,
      dimensions,
      soundwave,
      watermark,
      captions,
      creatorUserId: userId,
      layers: layerOrder,
      lockSpec,
      mainMediaContainer,
      mediaSegmentInfo,
      progress,
      timer,
      edgeVideos,
    }),
  );
};

export const prepareEmbedConfig = (exportConfigOverride: any) => (
  dispatch: Dispatch,
  getState: GetState,
) => {
  const mediaOffsetMillis = embedSelectors.transcriptMediaOffsetMillisSelector(
    getState(),
  );
  const updatedCaptions = dispatch(updateCaptionsOffset(mediaOffsetMillis));

  const edgeVideosConfig = dispatch(
    processIntroOutroClipUploads(exportConfigOverride),
  );

  const savedLayouts = dispatch(saveDirtyLayouts());

  return Promise.all([edgeVideosConfig, updatedCaptions, savedLayouts]).then(
    ([processIntroOutroOutput]) => {
      const { edgeVideos } = processIntroOutroOutput;
      // This is a workaround for avoiding overriding config for non export operations,
      // it should be reviewed when the feature is adjusted for advanced editor
      if (edgeVideos) {
        dispatch({ type: types.EMBED_EDGE_VIDEOS_CHANGE, payload: edgeVideos });
      }
    },
  );
};

interface CreateEmbedConfigurationFromStateOptions {
  /**
   * Override fields on the export config. Used by the export flow.
   */
  exportConfigOverride?: any;
  /**
   * Set by `doSaveConfiguration` to tag the dispatched `CREATE_REQUEST` with
   * `meta.autosaveDriven` so the preview-sync trigger suppresses its flush —
   * the originating IMMEDIATE/DELAYED action that kicked off the save already
   * scheduled one. Direct callers should leave this unset.
   */
  autosaveDriven?: boolean;
}

export const createEmbedConfigurationFromState = (
  options: CreateEmbedConfigurationFromStateOptions = {},
) => (dispatch: Dispatch) => {
  const { exportConfigOverride = {}, autosaveDriven = false } = options;
  const requestAction = { type: types.EMBED_CONFIGURATION_CREATE_REQUEST };
  dispatch(
    autosaveDriven ? withAutosaveDrivenMeta(requestAction) : requestAction,
  );

  const prepareEmbed = dispatch(prepareEmbedConfig(exportConfigOverride));

  const embedConfig = prepareEmbed.then(() => {
    return dispatch(createEmbedConfiguration());
  });

  const updatedProject = embedConfig.then(res => {
    dispatch(setEmbedConfiguration(res.response.configuration));
    dispatch(projectActions.updateCurrentProject());
  });

  return Promise.all([embedConfig, updatedProject, prepareEmbed])
    .then(([embedRes]) => {
      const { wid } = embedRes.response.configuration;
      dispatch(createEmbedCode(wid));
      dispatch({ type: types.EMBED_CONFIGURATION_CREATE_SUCCESS });
      return embedConfig;
    })
    .catch(errorAction => {
      dispatch({
        ...errorAction,
        type: types.EMBED_CONFIGURATION_CREATE_FAILURE,
      });
      return Promise.reject(errorAction);
    });
};

/**
 * NB: exported so that it can be used by the autosave middleware, but this function should
 *     _only_ be dispatched by the middleware.  to trigger a save, dispatch `saveConfiguration` or
 *     `debouncedSaveConfiguration`
 */
export const doSaveConfiguration = (dispatch: Dispatch) => {
  dispatch({ type: types.EMBED_CONFIGURATION_SAVE_REQUEST });

  return dispatch(createEmbedConfigurationFromState({ autosaveDriven: true }))
    .then(() => dispatch({ type: types.EMBED_CONFIGURATION_SAVE_SUCCESS }))
    .catch(err => {
      dispatch(
        notificationActions.showError({
          type: 'failedSave',
          code: 'ER001',
          message:
            'Embed configuration could not be saved. Press the button below to try again.',
        }),
      );
      dispatch({
        ...err,
        type: types.EMBED_CONFIGURATION_SAVE_FAILURE,
      });
    });
};

const immediateSaveAction = autosaveActionBuilder()
  .immediateSave()
  .build();

const delayedSaveAction = autosaveActionBuilder({
  type: types.EMBED_AUTOSAVE_SCHEDULE,
})
  .delayedSave()
  .build();

const cancelDelayedSaveAction = autosaveActionBuilder({
  type: types.EMBED_AUTOSAVE_SCHEDULE_CLEAR,
})
  .clear()
  .build();

export const saveConfiguration = () => immediateSaveAction;

export const debouncedSaveConfiguration = () => delayedSaveAction;

export const clearDebouncedSaveConfiguration = () => cancelDelayedSaveAction;

export const setEmbedRecording = (recordingId: number) => ({
  type: types.EMBED_RECORDING_SET,
  payload: { recordingId },
});

function handleSetEmbedDuration(dispatch: Dispatch, getState: GetState) {
  const currentDurationMillis = embedSelectors.embedDurationMillisSelector(
    getState(),
  );
  const slideEndMillis = embedSelectors.slideEndMillisSelector(getState());
  const textOverlayEndMillis = embedSelectors.textOverlayEndMillisSelector(
    getState(),
  );
  const audioEndMillis = embedSelectors.audioEndMillisSelector(getState());
  const videoEndMillis = embedSelectors.videoEndMillisSelector(getState());
  const waveformEndMillis = embedSelectors.soundwaveEndMillisSelector(
    getState(),
  );

  const durationMillis = max(
    slideEndMillis,
    textOverlayEndMillis,
    audioEndMillis,
    videoEndMillis,
    waveformEndMillis,
  );

  if (durationMillis !== currentDurationMillis) {
    dispatch(setEmbedDurationAction(durationMillis));
  }
}

/*
 * this is a naiive approach to finding the duration of the timeline.  debouncing in case of
 * performance issues with dense timelines.
 */
const throttledSetEmbedDuration = _.throttle(handleSetEmbedDuration, 200);

export const setEmbedDuration = (immediate = false) => (
  dispatch: Dispatch,
  getState: GetState,
) => {
  const fn = immediate ? handleSetEmbedDuration : throttledSetEmbedDuration;
  fn(dispatch, getState);
};

export default {
  clearEmbedState,
  createEmbedCode,
  getEmbedConfiguration,
  createEmbedConfigurationFromState,
  saveConfiguration,
  debouncedSaveConfiguration,
  setEmbedRecording,
  setEmbedDuration,
};
